import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { makeStyles } from '@material-ui/core/styles';
import { Content } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  teamMetricsApiRef,
  UIFrontendHealthResponse,
  UIPRWithTypedCoverage,
} from '../../../../../../api/teamMetricsApi';
import { HealthLoadingState } from './HealthLoadingState';
import { HealthNoDataState } from './HealthNoDataState';
import { DateRangePicker, DateRange, parseDateRangeFromParams, updateDateRangeParams } from './DateRangePicker';
import { ExcludeRepoDialog } from './ExcludeRepoDialog';
import { UserHealthDashboard } from './UserHealthDashboard';
import {
  FrontendHealthSummary,
  FrontendProjectTable,
  GithubTeamFilter,
  FrontendPRTable,
} from './frontend';

const FRONTEND_PROJECT_PREFIX = 'frontend@';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
  },
  titleSection: {},
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1A1A1A',
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: 14,
    color: '#8E9299',
  },
  divider: {
    borderTop: '1px solid #E2E8F0',
    margin: theme.spacing(4, 0),
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    borderRadius: 4,
    marginBottom: theme.spacing(3),
  },
  infoIcon: {
    fontSize: 16,
    color: '#3182CE',
    marginTop: 2,
  },
  infoText: {
    fontSize: 12,
    color: '#2B6CB0',
    lineHeight: 1.5,
  },
}));

interface FrontendHealthState {
  loading: boolean;
  error: Error | null;
  data: UIFrontendHealthResponse | null;
}

const FrontendHealthOverview: React.FC = () => {
  const classes = useStyles();
  const teamMetricsApi = useApi(teamMetricsApiRef);
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const parsed = parseDateRangeFromParams(new URLSearchParams(window.location.search));
    updateDateRangeParams(parsed);
    return parsed;
  });

  const [healthState, setHealthState] = useState<FrontendHealthState>({
    loading: true,
    error: null,
    data: null,
  });

  const [selectedGithubTeams, setSelectedGithubTeams] = useState<string[]>([]);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeProjectName, setExcludeProjectName] = useState('');
  const [isExcluding, setIsExcluding] = useState(false);
  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const refreshData = useCallback(async () => {
    try {
      // eslint-disable-next-line new-cap
      const response = await teamMetricsApi.GenerateFrontendHealthReport({
        startDate: dateRange.start,
      });
      setHealthState({
        loading: false,
        error: null,
        data: response,
      });
    } catch (err) {
      setHealthState({
        loading: false,
        error: err instanceof Error ? err : new Error('Failed to fetch frontend health data'),
        data: null,
      });
    }
  }, [dateRange.start, teamMetricsApi]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setHealthState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        // eslint-disable-next-line new-cap
        const response = await teamMetricsApi.GenerateFrontendHealthReport({
          startDate: dateRange.start,
        });

        if (!cancelled) {
          setHealthState({
            loading: false,
            error: null,
            data: response,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setHealthState({
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch frontend health data'),
            data: null,
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [dateRange.start, teamMetricsApi]);

  const { aggregateCoverageByType, projectSummaries, deploymentCount, pullRequests } = healthState.data ?? {
    aggregateCoverageByType: [],
    projectSummaries: [],
    deploymentCount: null,
    pullRequests: [],
  };

  const availableGithubTeams = useMemo(() => {
    const teamsSet = new Set<string>();
    projectSummaries.forEach((project) => {
      (project.githubTeamOwners ?? []).forEach((team) => teamsSet.add(team));
    });
    return Array.from(teamsSet).sort();
  }, [projectSummaries]);

  const filteredProjectSummaries = useMemo(() => {
    if (selectedGithubTeams.length === 0) return projectSummaries;
    return projectSummaries.filter((project) =>
      project.githubTeamOwners?.some((team) => selectedGithubTeams.includes(team))
    );
  }, [projectSummaries, selectedGithubTeams]);

  const filteredAggregateCoverage = useMemo(() => {
    if (selectedGithubTeams.length === 0) return aggregateCoverageByType;

    const testTypes = ['unit', 'component'];
    return testTypes.map((testType) => {
      const projectsWithType = filteredProjectSummaries.filter((p) =>
        p.coverageByType.some((c) => c.testType === testType)
      );

      if (projectsWithType.length === 0) {
        return {
          testType,
          metrics: {
            lines: { total: 0, covered: 0, pct: null },
            statements: { total: 0, covered: 0, pct: null },
            branches: { total: 0, covered: 0, pct: null },
            functions: { total: 0, covered: 0, pct: null },
          },
          statementsCoverageDelta: null,
        };
      }

      const totals = projectsWithType.reduce(
        (acc, project) => {
          const coverage = project.coverageByType.find((c) => c.testType === testType);
          if (coverage) {
            acc.statementsTotal += coverage.metrics.statements.total;
            acc.statementsCovered += coverage.metrics.statements.covered;
          }
          return acc;
        },
        { statementsTotal: 0, statementsCovered: 0 }
      );

      const pct = totals.statementsTotal > 0
        ? (totals.statementsCovered / totals.statementsTotal) * 100
        : null;

      return {
        testType,
        metrics: {
          lines: { total: 0, covered: 0, pct: null },
          statements: { total: totals.statementsTotal, covered: totals.statementsCovered, pct },
          branches: { total: 0, covered: 0, pct: null },
          functions: { total: 0, covered: 0, pct: null },
        },
        statementsCoverageDelta: null,
      };
    });
  }, [aggregateCoverageByType, filteredProjectSummaries, selectedGithubTeams]);

  const handleOpenExcludeDialog = useCallback((projectName: string) => {
    setExcludeProjectName(projectName);
    setExcludeDialogOpen(true);
  }, []);

  const handleCloseExcludeDialog = useCallback(() => {
    setExcludeDialogOpen(false);
    setExcludeProjectName('');
  }, []);

  const handleExcludeProject = useCallback(async (reason: string) => {
    setIsExcluding(true);
    try {
      // eslint-disable-next-line new-cap
      await teamMetricsApi.AddRepositoryExclusions({
        repoNames: [`${FRONTEND_PROJECT_PREFIX}${excludeProjectName}`],
        reason,
      });
      handleCloseExcludeDialog();
      setAlert({ message: `Project "${excludeProjectName}" has been excluded`, severity: 'success' });
      await refreshData();
    } catch (err) {
      setAlert({
        message: err instanceof Error ? err.message : 'Failed to exclude project',
        severity: 'error',
      });
    } finally {
      setIsExcluding(false);
    }
  }, [excludeProjectName, teamMetricsApi, handleCloseExcludeDialog, refreshData]);

  const handleIncludeProject = useCallback(async (projectName: string) => {
    setIsExcluding(true);
    try {
      // eslint-disable-next-line new-cap
      await teamMetricsApi.RemoveRepositoryExclusions({
        repoNames: [`${FRONTEND_PROJECT_PREFIX}${projectName}`],
      });
      setAlert({ message: `Project "${projectName}" has been included`, severity: 'success' });
      await refreshData();
    } catch (err) {
      setAlert({
        message: err instanceof Error ? err.message : 'Failed to include project',
        severity: 'error',
      });
    } finally {
      setIsExcluding(false);
    }
  }, [teamMetricsApi, refreshData]);

  const handleCloseAlert = useCallback(() => {
    setAlert(null);
  }, []);

  const handlePRActivityClick = useCallback(() => {
    const params = new URLSearchParams();
    params.set('start_date', dateRange.start);
    navigate(`prs?${params.toString()}`);
  }, [dateRange.start, navigate]);

  if (healthState.loading) {
    return <HealthLoadingState />;
  }

  if (healthState.error || !healthState.data) {
    return <HealthNoDataState context="team" entityName="acl-dev-team-frontend" />;
  }

  return (
    <Content className={classes.root}>
      <Box padding={4}>
        <Box className={classes.header}>
          <Box className={classes.titleSection}>
            <Typography className={classes.title}>Frontend Health</Typography>
            <Typography className={classes.subtitle}>
              Code coverage metrics for the frontend monorepo
            </Typography>
          </Box>

          <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
        </Box>

        <Box className={classes.infoBox}>
          <InfoOutlinedIcon className={classes.infoIcon} />
          <Typography className={classes.infoText}>
            <strong>Data Source:</strong> Coverage data is sourced from Github PRs merged to main and a nightly cron that runs a test suite.
            This shows project-level breakdown within the frontend monorepo, rather than the repo-by-repo
            analysis used for other teams.
          </Typography>
        </Box>

        {availableGithubTeams.length > 0 && (
          <Box mb={3}>
            <GithubTeamFilter
              selectedTeams={selectedGithubTeams}
              availableTeams={availableGithubTeams}
              onSelectionChange={setSelectedGithubTeams}
            />
          </Box>
        )}

        <FrontendHealthSummary
          aggregateCoverageByType={filteredAggregateCoverage}
          projectSummaries={filteredProjectSummaries}
          pullRequests={pullRequests}
          deploymentCount={deploymentCount}
          onPRActivityClick={handlePRActivityClick}
        />

        <FrontendProjectTable
          projectSummaries={filteredProjectSummaries}
          onExcludeProject={handleOpenExcludeDialog}
          onIncludeProject={handleIncludeProject}
          isExcluding={isExcluding}
        />
      </Box>

      <ExcludeRepoDialog
        open={excludeDialogOpen}
        repoName={excludeProjectName}
        onClose={handleCloseExcludeDialog}
        onExclude={handleExcludeProject}
        isLoading={isExcluding}
      />

      <Snackbar
        open={!!alert}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert?.severity} variant="filled">
          {alert?.message}
        </Alert>
      </Snackbar>
    </Content>
  );
};

interface FrontendPRsState {
  loading: boolean;
  error: Error | null;
  prs: UIPRWithTypedCoverage[];
}

const FrontendPRsPage: React.FC = () => {
  const classes = useStyles();
  const teamMetricsApi = useApi(teamMetricsApiRef);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const parsed = parseDateRangeFromParams(searchParams);
    return parsed;
  });

  const [prsState, setPrsState] = useState<FrontendPRsState>({
    loading: true,
    error: null,
    prs: [],
  });

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setPrsState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        // eslint-disable-next-line new-cap
        const response = await teamMetricsApi.GenerateFrontendHealthReport({
          startDate: dateRange.start,
        });

        if (!cancelled) {
          setPrsState({
            loading: false,
            error: null,
            prs: response.pullRequests,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setPrsState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch PR data'),
          }));
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [dateRange.start, teamMetricsApi]);

  const handleDateRangeChange = useCallback((newDateRange: DateRange) => {
    setDateRange(newDateRange);
    const params = new URLSearchParams(searchParams);
    params.set('start_date', newDateRange.start);
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleBack = useCallback(() => {
    navigate('..');
  }, [navigate]);

  const handleAuthorClick = useCallback((weaveEmail: string) => {
    navigate(`../user/${encodeURIComponent(weaveEmail)}`);
  }, [navigate]);

  if (prsState.loading) {
    return <HealthLoadingState />;
  }

  return (
    <Content className={classes.root}>
      <FrontendPRTable
        prs={prsState.prs}
        title="All Frontend PRs"
        subtitle="Pull requests merged to the frontend monorepo"
        onBack={handleBack}
        onAuthorClick={handleAuthorClick}
        dateRangePicker={<DateRangePicker dateRange={dateRange} onChange={handleDateRangeChange} />}
      />
    </Content>
  );
};

const FrontendUserHealthPage: React.FC = () => {
  const classes = useStyles();
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  if (!username) {
    return <HealthNoDataState context="user" entityName="Unknown" />;
  }

  return (
    <Content className={classes.root}>
      <UserHealthDashboard
        weaveEmail={decodeURIComponent(username)}
        onBack={handleBack}
      />
    </Content>
  );
};

export const FrontendTeamHealth: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<FrontendHealthOverview />} />
      <Route path="/prs" element={<FrontendPRsPage />} />
      <Route path="/user/:username" element={<FrontendUserHealthPage />} />
    </Routes>
  );
};

export default FrontendTeamHealth;
